import type { CollectionStatus } from '../../services/collectionConfigService';

// Pure status → headline mapping for the Payment collection page. Never renders
// "no data" or "untested" as connected (rule: no data is not good news).
export type CollectionHeadline = {
  state: 'live' | 'ready' | 'disabled' | 'untested' | 'failing' | 'not_configured' | 'unavailable' | 'unknown';
  label: string;
  tone: 'success' | 'processing' | 'warning' | 'error' | 'default';
  detail: string;
};

export function collectionHeadline(s: CollectionStatus | null | undefined, loadFailed = false): CollectionHeadline {
  if (loadFailed || !s) {
    return { state: 'unknown', label: 'Status unavailable', tone: 'warning', detail: 'Could not load collection status — this is not an all-clear.' };
  }
  if (s.source === 'none' || (!s.api_key_set && s.active_version == null)) {
    return { state: 'not_configured', label: 'Not configured', tone: 'default', detail: 'No M-Pesa credentials — customers see "Online payment is not available yet".' };
  }
  const tested = s.last_test && s.last_test.version === s.active_version;
  if (tested && s.last_test!.ok === false) {
    return { state: 'failing', label: 'Connection failing', tone: 'error', detail: s.last_test!.message || 'The last connection test failed.' };
  }
  if (s.available) {
    return {
      state: 'live',
      label: s.environment === 'live' ? 'Collecting (live)' : 'Collecting (sandbox)',
      tone: 'success',
      detail: `Customers can pay with M-Pesa — credential v${s.active_version} (${s.source === 'env' ? 'environment fallback' : 'saved here'}).`,
    };
  }
  if (!tested && s.source === 'db') {
    return { state: 'untested', label: 'Not tested', tone: 'warning', detail: 'Run a connection test on these credentials, then enable.' };
  }
  if (!s.enabled) {
    return { state: 'disabled', label: 'Disabled', tone: 'default', detail: s.unavailable_reason || 'Direct M-Pesa is disabled.' };
  }
  return { state: 'unavailable', label: 'Not available', tone: 'warning', detail: s.unavailable_reason || 'Direct M-Pesa is not available.' };
}

/** Audit action → operator-readable label. */
export function auditLabel(action: string): string {
  return (
    {
      'credentials.saved': 'Credentials saved',
      'credentials.rotated': 'Credentials rotated',
      'connection.tested': 'Connection tested',
      'collection.enabled': 'Enabled',
      'collection.disabled': 'Disabled',
    } as Record<string, string>
  )[action] ?? action;
}
